
import React, { useState } from 'react';
import { X, Shield, Lock, Activity, Server, Database, Cpu, ExternalLink, Award, Volume2, VolumeX } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  type: 'terms' | 'lgpd' | 'status' | 'reference';
}

const LegalModals: React.FC<ModalProps> = ({ isOpen, onClose, title, type }) => {
  const [isMuted, setIsMuted] = useState(true);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose}></div>
      <div className="relative w-full max-w-4xl bg-zinc-950 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in duration-300">
        <div className="flex justify-between items-center p-6 border-b border-zinc-900 bg-zinc-900/50">
          <div className="flex items-center space-x-3">
            {type === 'terms' && <Shield className="text-[#c17c0c]" size={20} />}
            {type === 'lgpd' && <Lock className="text-[#c17c0c]" size={20} />}
            {type === 'status' && <Activity className="text-green-500" size={20} />}
            {type === 'reference' && <Award className="text-[#c17c0c]" size={20} />}
            <h3 className="font-tactical font-bold uppercase tracking-widest text-white">{title}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>
        
        <div className="p-8 max-h-[80vh] overflow-y-auto text-zinc-400 text-sm leading-relaxed font-light custom-scrollbar">
          {type === 'terms' && (
            <div className="max-w-2xl mx-auto space-y-6">
              <section>
                <h4 className="text-white font-bold mb-2 uppercase text-xs">1. Licença de Uso Estratégico</h4>
                <p>O Guru.voto concede uma licença limitada, não exclusiva e intransferível para o uso da plataforma durante o ciclo eleitoral vigente. O uso é estritamente restrito a atividades de planejamento político e institucional em conformidade com as resoluções do TSE.</p>
              </section>
              <section>
                <h4 className="text-white font-bold mb-2 uppercase text-xs">2. Restrições de Territorialidade</h4>
                <p>O sistema é configurado por ID Territorial. Tentativas de acesso a dados de zonas não contratadas ou extração em massa de dados geoespaciais (scraping) resultará em bloqueio imediato do Protocolo War Room sem direito a reembolso.</p>
              </section>
              <section>
                <h4 className="text-white font-bold mb-2 uppercase text-xs">3. Propriedade Intelectual</h4>
                <p>Todos os algoritmos de IA Prescritiva, modelos de hexágonos H3 customizados e interfaces são propriedade exclusiva da Guru Intelligence Ops.</p>
              </section>
            </div>
          )}

          {type === 'lgpd' && (
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-xl mb-6">
                <p className="text-green-500 text-xs font-mono uppercase tracking-tighter">Status: Compliance Total (Res. 23.677/2021 TSE)</p>
              </div>
              <section>
                <h4 className="text-white font-bold mb-2 uppercase text-xs">Tratamento de Dados de Eleitores</h4>
                <p>O Guru.voto não armazena dados pessoais identificáveis (PII) de eleitores individuais. Todas as análises são baseadas em microdados anonimizados fornecidos pelo Portal de Dados Abertos do TSE e IBGE, processados em camadas geoespaciais agregadas.</p>
              </section>
              <section>
                <h4 className="text-white font-bold mb-2 uppercase text-xs">Segurança de Gabinete</h4>
                <p>Os dados de estratégia (anotações, mapas de calor privados e alianças) são criptografados ponta-a-ponta (AES-256). Nem mesmo nossos engenheiros têm acesso às camadas de estratégia do usuário.</p>
              </section>
            </div>
          )}

          {type === 'status' && (
            <div className="max-w-2xl mx-auto space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center space-x-2">
                      <Database size={14} className="text-zinc-500" />
                      <span className="text-xs uppercase font-mono">BigQuery Engine</span>
                    </div>
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  </div>
                  <div className="text-xl font-bold font-tactical text-white">99.9% ONLINE</div>
                </div>
                <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center space-x-2">
                      <Cpu size={14} className="text-zinc-500" />
                      <span className="text-xs uppercase font-mono">Vertex AI Core</span>
                    </div>
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  </div>
                  <div className="text-xl font-bold font-tactical text-white">OPERACIONAL</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className="text-white font-bold uppercase text-[10px] tracking-widest flex items-center">
                  <Server size={14} className="mr-2" /> Sistemas de Retaguarda
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 border-b border-zinc-900">
                    <span className="text-xs font-mono">Geoprocessamento H3</span>
                    <span className="text-green-500 text-[10px] font-bold">NORMAL (12ms)</span>
                  </div>
                  <div className="flex justify-between items-center p-2 border-b border-zinc-900">
                    <span className="text-xs font-mono">Simulador de Sobras</span>
                    <span className="text-green-500 text-[10px] font-bold">ESTÁVEL</span>
                  </div>
                  <div className="flex justify-between items-center p-2 border-b border-zinc-900">
                    <span className="text-xs font-mono">API de Mapas (PyDeck)</span>
                    <span className="text-green-500 text-[10px] font-bold">CONECTADO</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {type === 'reference' && (
            <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
              {/* Seção Mentor */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
                <div className="md:col-span-4 relative group">
                  <div className="absolute -inset-2 bg-gradient-to-br from-[#c17c0c] to-black rounded-3xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
                  <div className="relative aspect-[3/4] overflow-hidden rounded-2xl border border-zinc-800 shadow-2xl">
                    <img 
                      src="https://legislativo.pro/img/bia.webp" 
                      alt="Roberto Biagioni" 
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-105 group-hover:scale-100"
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black via-black/40 to-transparent">
                      <p className="text-white font-bold text-lg leading-none uppercase italic tracking-tighter">Roberto Biagioni</p>
                      <p className="text-[#c17c0c] text-[10px] font-mono uppercase tracking-widest mt-1">Conselho Estratégico</p>
                    </div>
                  </div>
                </div>
                
                <div className="md:col-span-8 space-y-6">
                  <div>
                    <h4 className="text-[#c17c0c] font-bold text-2xl uppercase italic tracking-tighter mb-4">O Mentor do Seu "War Room" Digital</h4>
                    <p className="text-zinc-300 leading-relaxed">
                      A inteligência preditiva do Guru.voto é fundamentada na visão de quem construiu infraestruturas críticas por três décadas. O Conselho Estratégico do Grupo Planeta Voto é presidido por <strong>Roberto Biagioni</strong>, o idealizador da metodologia que transformou a análise política em ciência de dados. O DNA de segurança e precisão técnica — marcas de sua trajetória — permanecem como o pilar central de nossas soluções.
                    </p>
                  </div>

                  <div className="p-6 bg-zinc-900/30 border-l-2 border-[#c17c0c] rounded-r-xl">
                    <h5 className="text-white font-bold uppercase text-xs mb-2 flex items-center">
                      <Shield size={14} className="mr-2 text-[#c17c0c]" /> Do Sistema Financeiro à Guerra Eleitoral
                    </h5>
                    <p className="text-sm text-zinc-400">
                      A robustez do Guru.voto herda a expertise de seu criador em ambientes de missão crítica. Em meados dos anos 2000, Roberto desenvolveu a patente <strong>INPI BR PI0404074-0</strong>, tecnologia de "impressão digital de hardware" que deu origem a "Autenticação de 2 fatores" adotada inicialmente por <strong>Banco do Brasil</strong> e <strong>Caixa Econômica Federal</strong>. Foi essa inovação que alavancou a blindagem de milhões de transações contra fraudes. Hoje, essa mesma lógica de Segurança da Informação também orienta o desenvolvimento do Guru.voto.
                    </p>
                  </div>
                </div>
              </div>

              {/* Grid de Experiência */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-8 border-y border-zinc-900">
                <div className="space-y-4">
                  <h5 className="text-white font-bold uppercase text-xs tracking-widest">Pioneirismo Tecnológico</h5>
                  <p className="text-sm text-zinc-500">
                    Pioneiro na implementação da internet comercial em Minas Gerais (via Biamar Networks/Via Real), <strong>Biagioni</strong> construiu as "estradas digitais" por onde os dados trafegam muito antes da era do Big Data. Esse domínio sobre infraestrutura permite ao Guru.voto processar dados em granularidade H3 com estabilidade militar.
                  </p>
                </div>
                <div className="space-y-4">
                  <h5 className="text-white font-bold uppercase text-xs tracking-widest">A Ciência da Persuasão Política</h5>
                  <p className="text-sm text-zinc-500">
                    Como Conselheiro, <strong>Biagioni</strong> aporta ao grupo a vivência de uma década na Inteligência Estratégica atuando na <strong>Assembleia Legislativa de Minas Gerais (ALMG)</strong> bem como na condução de inúmeras campanhas eleitorais desde o início dos anos 2000. Essa experiência que foi sistematizada em sua obra <strong>"PoliBIAtic - O Xadrez da Persuasão"</strong> (disponível em <a href="https://politicando.online" target="_blank" rel="noopener noreferrer" className="text-[#c17c0c] hover:underline">politicando.online</a>) no quesito comportamento político, hoje serve como a "regra de negócio" dos algoritmos na forma “fonte de informações” e, é o que da forma ao Guru.voto.
                  </p>
                </div>
              </div>

              {/* Seção Legislativo.pro */}
              <div className="pt-4">
                <div className="flex flex-col lg:flex-row gap-10 items-center bg-gradient-to-br from-[#c17c0c]/10 to-transparent p-8 rounded-3xl border border-[#c17c0c]/20">
                  <div className="lg:w-1/2 space-y-6">
                    <h4 className="text-white font-bold text-3xl uppercase italic tracking-tighter leading-tight">O Próximo Passo: <br/><span className="text-[#c17c0c]">Da Vitória à Governança</span></h4>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      A eleição é a batalha pela conquista do poder; o mandato é a guerra pela manutenção da relevância. Sob o mesmo Conselho Estratégico que desenhou o Guru.voto, oferecemos a solução definitiva para o "day after" da vitória.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <div className="mt-1 w-1.5 h-1.5 rounded-full bg-[#c17c0c]"></div>
                        <div>
                          <p className="text-white text-xs font-bold uppercase tracking-widest">🏛️ LEGISLATIVO.PRO</p>
                          <p className="text-[11px] text-zinc-500">Gestão de Mandatos de Alta Performance. Transforme burocracia em inteligência política.</p>
                        </div>
                      </div>
                    </div>

                    <a 
                      href="https://legislativo.pro" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 bg-[#c17c0c] text-white px-6 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all group"
                    >
                      <span>Conheça a Engenharia do Mandato</span>
                      <ExternalLink size={14} className="group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>

                  <div className="lg:w-1/2 flex justify-center relative">
                    <div className="absolute -inset-1 bg-[#c17c0c]/30 rounded-2xl blur-md"></div>
                    <div className="relative w-full max-w-[280px] aspect-[9/16] rounded-2xl overflow-hidden border-2 border-zinc-800 bg-black shadow-2xl group/video">
                      <video 
                        src="https://legislativo.pro/img/pri_completo.mp4" 
                        autoPlay 
                        loop 
                        muted={isMuted}
                        playsInline
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-0 left-0 w-full h-full pointer-events-none border-[8px] border-zinc-950/80"></div>
                      
                      {/* Video Controls Overlay */}
                      <button 
                        onClick={() => setIsMuted(!isMuted)}
                        className="absolute top-4 left-4 bg-black/60 hover:bg-[#c17c0c]/80 backdrop-blur-md p-2 rounded-full border border-zinc-800 text-white transition-all z-10"
                        title={isMuted ? "Ativar som" : "Desativar som"}
                      >
                        {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                      </button>

                      <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md px-3 py-1 rounded-md border border-zinc-800">
                        <span className="text-[8px] font-mono text-[#c17c0c] uppercase tracking-widest">legislativo_demo.mp4</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-6 bg-zinc-900/50 border-t border-zinc-900 flex justify-end">
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-full text-xs font-bold uppercase tracking-widest transition-all"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalModals;
